import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Public: active hero slides for the homepage
export const fetchHeroSlides = createAsyncThunk(
  'hero/fetchHeroSlides',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/hero');
      return response.data.data.slides;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch hero slides');
    }
  }
);

// Admin: all slides (including inactive)
export const fetchAllHeroSlides = createAsyncThunk(
  'hero/fetchAllHeroSlides',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/hero/admin/all');
      return response.data.data.slides;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch hero slides');
    }
  }
);

// Admin: Create slide
export const createHeroSlide = createAsyncThunk(
  'hero/createHeroSlide',
  async (slideData, { rejectWithValue }) => {
    try {
      const config = slideData instanceof FormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};
      const response = await api.post('/hero', slideData, config);
      return response.data.data.slide;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create hero slide');
    }
  }
);

// Admin: Update slide
export const updateHeroSlide = createAsyncThunk(
  'hero/updateHeroSlide',
  async ({ id, slideData }, { rejectWithValue }) => {
    try {
      const config = slideData instanceof FormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};
      const response = await api.put(`/hero/${id}`, slideData, config);
      return response.data.data.slide;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update hero slide');
    }
  }
);

// Admin: Delete slide
export const deleteHeroSlide = createAsyncThunk(
  'hero/deleteHeroSlide',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/hero/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete hero slide');
    }
  }
);

const initialState = {
  slides: [],       // public active slides (homepage)
  allSlides: [],    // admin list
  isLoading: false,
  error: null,
};

const heroSlice = createSlice({
  name: 'hero',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch active slides
      .addCase(fetchHeroSlides.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchHeroSlides.fulfilled, (state, action) => {
        state.isLoading = false;
        state.slides = action.payload;
      })
      .addCase(fetchHeroSlides.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch all slides (admin)
      .addCase(fetchAllHeroSlides.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllHeroSlides.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allSlides = action.payload;
      })
      .addCase(fetchAllHeroSlides.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createHeroSlide.fulfilled, (state, action) => {
        state.allSlides.push(action.payload);
      })
      // Update
      .addCase(updateHeroSlide.fulfilled, (state, action) => {
        const index = state.allSlides.findIndex(s => s._id === action.payload._id);
        if (index !== -1) {
          state.allSlides[index] = action.payload;
        }
      })
      // Delete
      .addCase(deleteHeroSlide.fulfilled, (state, action) => {
        state.allSlides = state.allSlides.filter(s => s._id !== action.payload);
      });
  },
});

export default heroSlice.reducer;
